import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { categories, articles } from "@/mocks/articles";

interface SEOMetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
  articleData?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    tags?: string[];
  };
}

export function generatePageTitle(title?: string): string {
  const base = "B站暴躁少女CSGO视频 - 精选内容、资讯与观看指南";
  return title ? `${title} - ${base}` : base;
}

export default function SEOMeta({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = "website",
  ogImage,
  articleData,
}: SEOMetaProps) {
  const location = useLocation();
  const fullTitle = generatePageTitle(title);
  const defaultDescription = "B站暴躁少女CSGO视频专题站，汇聚CSGO精彩操作、搞笑集锦、新手教学、赛事资讯。了解热门视频内容、观看方法与常见问题，发现更多值得关注的CSGO资讯。";
  const defaultKeywords = "B站暴躁少女CSGO视频,CSGO,暴躁少女,视频,热门内容,最新资讯,观看指南";
  const baseUrl = "https://baozhaocsgo.cn";
  const canonical = canonicalPath ? `${baseUrl}${canonicalPath}` : `${baseUrl}${location.pathname}`;
  const desc = description || defaultDescription;
  const kw = keywords || defaultKeywords;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", desc);
    setMeta("keywords", kw);
    setMeta("og:title", fullTitle, true);
    setMeta("og:description", desc, true);
    setMeta("og:type", ogType, true);
    setMeta("og:url", canonical, true);
    setMeta("og:site_name", "B站暴躁少女CSGO视频专题站", true);
    setMeta("og:locale", "zh_CN", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", desc);

    if (ogImage) {
      setMeta("og:image", ogImage, true);
      setMeta("twitter:image", ogImage);
    }

    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute("href", canonical);

    if (articleData) {
      const setArticleMeta = (name: string, content: string) => {
        let el = document.querySelector(`meta[property="${name}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute("property", name);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      };
      if (articleData.publishedTime) setArticleMeta("article:published_time", articleData.publishedTime);
      if (articleData.modifiedTime) setArticleMeta("article:modified_time", articleData.modifiedTime);
      if (articleData.author) setArticleMeta("article:author", articleData.author);
      if (articleData.tags) {
        articleData.tags.forEach((tag) => {
          let tagEl = document.querySelector(`meta[property="article:tag"][content="${tag}"]`);
          if (!tagEl) {
            tagEl = document.createElement("meta");
            tagEl.setAttribute("property", "article:tag");
            tagEl.setAttribute("content", tag);
            document.head.appendChild(tagEl);
          }
        });
      }
    }

    return () => {
      // Cleanup not strictly necessary but good practice
    };
  }, [fullTitle, desc, kw, ogType, canonical, ogImage, articleData]);

  return null;
}